import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { Icon, Typography } from '@statsbomb/kitbag-components';
import { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { SpokeIcon } from '../icons/SpokeIcon';
import { SelectedListItem } from './SelectedListItem';

// Uses dnd kit - Reference: https://docs.dndkit.com/

export const SortableListItem = ({
  id,
  children,
  onClick,
  onRemove,
  numberOfSpokes,
  spokeIndex,
  testId,
  ariaLabel,
  isActive,
  isSortable,
  isLocked,
}: {
  id: string;
  children: ReactNode;
  onClick?: () => void;
  onRemove?: () => void;
  numberOfSpokes?: number;
  spokeIndex?: number;
  testId?: string;
  ariaLabel?: string;
  isActive?: boolean;
  isSortable?: boolean;
  isLocked?: boolean;
}) => {
  const { t } = useTranslation('accessibility');
  const isDraggable = isSortable && !isLocked;
  const { attributes, listeners, setNodeRef, transform, transition } = useSortable({
    id,
    disabled: isDraggable ? undefined : { draggable: true, droppable: true },
  });
  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
  };
  const hasSpoke = numberOfSpokes !== undefined && spokeIndex !== undefined;

  return (
    <SelectedListItem
      onClick={onClick}
      onRemove={onRemove}
      style={style}
      ref={setNodeRef}
      testId={testId}
      ariaLabel={ariaLabel}
      isActive={isActive}
    >
      <div className="flex items-center gap-2 flex-1 min-w-0 text-left">
        {isLocked ? (
          <span
            role="img"
            className="opacity-sb-overlay"
            aria-label={t('listItem.locked', {
              item: ariaLabel || t('listItem.item'),
              interpolation: { escapeValue: false },
            })}
          >
            <Icon colour="primary" variant="Lock" size="small" />
          </span>
        ) : (
          isDraggable && (
            <span
              className="!cursor-grab touch-none"
              aria-label={t('listItem.drag', {
                item: ariaLabel || t('listItem.item'),
                interpolation: { escapeValue: false },
              })}
              {...attributes}
              {...listeners}
            >
              <Icon colour="primary" variant="DragHandle" size="small" />
            </span>
          )
        )}
        {hasSpoke && <SpokeIcon numberOfSpokes={numberOfSpokes} spokeIndex={spokeIndex} />}
        <Typography variant="bodyMedium" as="span" className="flex items-center justify-between flex-1 min-w-0">
          {children}
        </Typography>
      </div>
    </SelectedListItem>
  );
};
